import {
  useMutation,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getUserLinks,
  createLink,
  deleteLinks,
  updateLink,
  updateMetaTitleDesc,
  uploadMetaImage,
  addToFavorites,
  removeFromFavorites,
  isLinkAvailable,
  getYouTubeStatisticsForLink,
  subscribeUseToPlan,
  subscribePlan,
} from '../../services/linkService';
import { uploadBrandImageToS3 } from '../../services/userService';
import axiosInstance from '../../config/axiosConfig';
import { queryKeys } from './queryKeys';

const STALE_TIME = 5 * 60 * 1000;

export const useUserLinksQuery = (
  userId: string,
  sortBy: string,
  page: number,
  limit: number,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.links.byUser(userId, { sortBy, page, limit }),
    queryFn: () => getUserLinks(userId, { sortBy, page, limit }),
    enabled: enabled && !!userId,
    staleTime: STALE_TIME,
  });

export const useUserLinksInfiniteQuery = (
  userId: string,
  sortBy: string,
  search: string,
  limit = 9,
) =>
  useInfiniteQuery({
    queryKey: queryKeys.links.byUser(userId, { sortBy, search, limit }),
    queryFn: ({ pageParam }) =>
      getUserLinks(userId, {
        sortBy,
        page: pageParam as number,
        limit,
        search,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.metadata?.hasMore) {
        return lastPage.metadata.currentPage + 1;
      }
      return undefined;
    },
    enabled: !!userId,
    staleTime: STALE_TIME,
  });

export const useCreateLinkMutation = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (url: string) => createLink(userId, url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links', userId] });
    },
  });
};

export const useDeleteLinksMutation = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkIds: string[]) =>
      deleteLinks(userId, linkIds) as Promise<any>,
    onSuccess: (_data, deletedIds) => {
      queryClient.setQueriesData(
        { queryKey: ['links', userId] },
        (old: any) => {
          if (!old) return old;
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                links: (page.links || []).filter(
                  (l: any) => !deletedIds.includes(l._id),
                ),
              })),
            };
          }
          if (old.links) {
            return {
              ...old,
              links: old.links.filter((l: any) => !deletedIds.includes(l._id)),
            };
          }
          return old;
        },
      );
      queryClient.invalidateQueries({ queryKey: ['links', userId] });
    },
  });
};

export const useUpdateLinkMutation = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (link: any) => updateLink(userId, link) as Promise<any>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links', userId] });
    },
  });
};

export const useUpdateMetaTitleDescMutation = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      linkId,
      metaTitle,
      metaDescription,
    }: {
      linkId: string;
      metaTitle: string;
      metaDescription: string;
    }) => updateMetaTitleDesc(userId, linkId, metaTitle, metaDescription),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links', userId] });
    },
  });
};

export const useUploadMetaImageMutation = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      linkId,
      formData,
    }: {
      linkId: string;
      formData: FormData;
    }) => uploadMetaImage(userId, linkId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links', userId] });
    },
  });
};

export const useAddToFavoritesMutation = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => addToFavorites(linkId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links', userId] });
    },
  });
};

export const useRemoveFromFavoritesMutation = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => removeFromFavorites(linkId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links', userId] });
    },
  });
};

export const useIsLinkAvailableQuery = (
  userId: string,
  linkId: string,
  shortUrl: string,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.links.isAvailable(userId, linkId, shortUrl),
    queryFn: () => isLinkAvailable(userId, linkId, shortUrl),
    enabled: enabled && !!userId && !!linkId && !!shortUrl,
  });

export const useUploadBrandImageMutation = () =>
  useMutation({
    mutationFn: (file: File) => uploadBrandImageToS3(file),
  });

export const useYouTubeStatisticsQuery = (
  userId: string,
  linkId: string,
  interval: string,
) =>
  useQuery({
    queryKey: queryKeys.links.youtubeStats(userId, linkId, interval),
    queryFn: () => getYouTubeStatisticsForLink(userId, linkId, interval),
    enabled: !!userId && !!linkId,
    staleTime: STALE_TIME,
  });

export const useSubscribeUserToPlanMutation = () =>
  useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: { plan: import('../../types/interfaces').SubscriptionPlan };
    }) => subscribeUseToPlan(userId, data),
  });

export const useSubscribePlanMutation = () =>
  useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: any }) =>
      subscribePlan(userId, data),
  });

export const useBulkUploadLinksMutation = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      axiosInstance
        .post(`/users/${userId}/links/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links', userId] });
    },
  });
};
